import {
  $createParagraphNode,
  $getRoot,
  $isElementNode,
  $isLineBreakNode,
  $isParagraphNode,
  $isTextNode,
  type LexicalNode,
} from 'lexical';

type SerializedLexicalNode = {
  type?: string;
  children?: SerializedLexicalNode[];
  text?: string;
};

type SerializedEditorState = {
  root?: SerializedLexicalNode;
};

function isEmptyTextNode(node: SerializedLexicalNode): boolean {
  return node.type === 'text' && (node.text ?? '') === '';
}

function isTrailingNoiseNode(node: SerializedLexicalNode): boolean {
  return node.type === 'linebreak' || isEmptyTextNode(node);
}

function isEmptyParagraphJson(node: SerializedLexicalNode): boolean {
  if (node.type !== 'paragraph') {
    return false;
  }

  const children = node.children ?? [];
  if (children.length === 0) {
    return true;
  }

  return children.every(
    (child) => isEmptyTextNode(child) || child.type === 'linebreak',
  );
}

function trimTrailingNoiseFromParagraphJson(
  paragraph: SerializedLexicalNode,
): SerializedLexicalNode {
  const children = [...(paragraph.children ?? [])];

  while (children.length > 0) {
    const lastChild = children[children.length - 1];
    if (!lastChild || !isTrailingNoiseNode(lastChild)) {
      break;
    }
    children.pop();
  }

  return {
    ...paragraph,
    children,
  };
}

export function normalizeSerializedEditorStateForHistory(
  serialized: string,
): string {
  try {
    const parsed = JSON.parse(serialized) as SerializedEditorState;
    const root = parsed.root;
    if (!root || !Array.isArray(root.children)) {
      return serialized;
    }

    let children = root.children.map(trimTrailingNoiseFromParagraphJson);

    let first = children[0];
    while (children.length > 1 && first && isEmptyParagraphJson(first)) {
      children = children.slice(1);
      first = children[0];
    }

    let last = children[children.length - 1];
    while (children.length > 1 && last && isEmptyParagraphJson(last)) {
      children = children.slice(0, -1);
      last = children[children.length - 1];
    }

    if (children.length === 0) {
      children = [{ type: 'paragraph', children: [] }];
    }

    return JSON.stringify({
      ...parsed,
      root: {
        ...root,
        children,
      },
    });
  } catch {
    return serialized;
  }
}

function isEmptyParagraphNode(node: LexicalNode): boolean {
  if (!$isParagraphNode(node)) {
    return false;
  }

  const text = node.getTextContent();
  return text === '' || text === '\n';
}

function trimTrailingNoiseFromParagraph(node: LexicalNode): void {
  if (!$isElementNode(node)) {
    return;
  }

  let child = node.getLastChild();
  while (child !== null) {
    const shouldRemove =
      $isLineBreakNode(child) ||
      ($isTextNode(child) && child.getTextContent() === '');

    if (!shouldRemove) {
      break;
    }

    const previous = child.getPreviousSibling();
    child.remove();
    child = previous;
  }
}

export function removeEmptyEdgeParagraphs() {
  const root = $getRoot();

  let first = root.getFirstChild();
  while (root.getChildrenSize() > 1 && first && isEmptyParagraphNode(first)) {
    first.remove();
    first = root.getFirstChild();
  }

  let last = root.getLastChild();
  while (root.getChildrenSize() > 1 && last && isEmptyParagraphNode(last)) {
    last.remove();
    last = root.getLastChild();
  }

  const trailingParagraph = root.getLastChild();
  if (trailingParagraph) {
    trimTrailingNoiseFromParagraph(trailingParagraph);
  }

  if (root.getChildrenSize() === 0) {
    root.append($createParagraphNode());
  }
}

export function resetEditorToEmptyParagraph() {
  const root = $getRoot();
  root.clear();
  root.append($createParagraphNode());
}
